import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { matchPath } from 'react-router'
import { Sidebar } from 'rsuite'
import Sidenav from 'rsuite/Sidenav'
import Nav from 'rsuite/Nav'
import SignOutIcon from '@rsuite/icons/legacy/SignOut'
import MenuItems from 'config/MenuItems'
import tenantConfig from 'config/tenant'
import { Divider } from 'shared/atoms'
import ConfirmDialog from '../confirmDialog/ConfirmDialog'
import { useAuth } from 'contexts/AuthContext'
import { notifySuccess } from 'utils'
import NavItem from './NavItem'
import './sideNav.scss'

interface SideNavProps {
  /** Phone-only: whether the off-canvas nav is slid in. Ignored on desktop. */
  mobileOpen?: boolean;
  /** Phone-only: called when a nav item is tapped so the parent can close. */
  onMobileClose?: () => void;
}

const SideNav: React.FC<SideNavProps> = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { roles, hasAnyPermission, isModuleEnabled, logout } = useAuth()
  const [activeKey, setActiveKey] = React.useState('')
  // Confirm sign-out before clearing the session, so an accidental click
  // doesn't log the user out. Cancel just closes the dialog.
  const [signOutOpen, setSignOutOpen] = React.useState(false)

  const handleSignOut = () => {
    onMobileClose?.()
    logout()
    navigate('/login', { replace: true })
    notifySuccess('Signed out successfully')
  }

  // SUPER_ADMIN owns module configuration, so they need to see every item even
  // when the module is currently disabled — otherwise they couldn't navigate
  // to the screens that turn things on. ADMIN and below stay gated.
  const isSuperAdmin = roles.includes('ROLE_SUPER_ADMIN')

  // Item shows iff role + permission + module gates all pass. A gate is skipped
  // when the corresponding field is omitted on the menu entry, and the module
  // gate is skipped entirely for SUPER_ADMIN.
  const visibleItems = React.useMemo(() => {
    const passesRole = (item: (typeof MenuItems)[number]) =>
      !item.allowedRoles ||
      item.allowedRoles.length === 0 ||
      item.allowedRoles.some((r) => roles.includes(r))
    const passesPermission = (item: (typeof MenuItems)[number]) =>
      !item.requiredPermissions ||
      item.requiredPermissions.length === 0 ||
      hasAnyPermission(item.requiredPermissions)
    const passesModule = (item: (typeof MenuItems)[number]) =>
      isSuperAdmin ||
      !item.requiredModule ||
      isModuleEnabled(item.requiredModule)
    return MenuItems.filter(
      (i) => passesRole(i) && passesPermission(i) && passesModule(i)
    )
  }, [roles, hasAnyPermission, isModuleEnabled, isSuperAdmin])

  useEffect(() => {
    const currentPath = location.pathname

    const findActiveMenuItem = (
      items: typeof MenuItems,
      path: string
    ): string | undefined => {
      // Sort by link length (longest first) so a more specific entry like
      // `/admin/modules` wins over a shorter prefix like `/admin` when both
      // exist. Without this, prefix matching could highlight the wrong tab.
      const sorted = [...items].sort(
        (a, b) => (b.link?.length ?? 0) - (a.link?.length ?? 0)
      )
      for (const item of sorted) {
        // `end: false` lets `/plots` match `/plots/123`, so detail pages keep
        // the right sidebar tab highlighted.
        if (item.link && matchPath({ path: item.link, end: false }, path)) {
          return item.id
        }
        if (item.matchPaths?.some((p) => matchPath({ path: p, end: false }, path))) {
          return item.id
        }
        if (item.children) {
          const childActiveKey = findActiveMenuItem(item.children, path)
          if (childActiveKey) {
            return item.id
          }
        }
      }
      return undefined
    }

    const activeMenuItemId = findActiveMenuItem(MenuItems, currentPath)
    if (activeMenuItemId) {
      setActiveKey(activeMenuItemId)
    }
  }, [location.pathname])

  // Sidebar starts open. It collapses/expands only via the toggle button —
  // clicking elsewhere on the page leaves it as-is.
  const [expanded, setExpanded] = React.useState(true)
  const clickToggler = () => {
    setExpanded(!expanded)
  }

  // On phones the off-canvas nav must always show labels when slid in, so
  // suppress the `collapsing` (icon-only) class while `mobile-open`. On desktop
  // `mobileOpen` is always false, so the collapse logic is unchanged.
  let sidebarClass = expanded ? '' : 'collapsing'
  if (mobileOpen) sidebarClass = 'mobile-open'

  return (
    <Sidebar
      className={sidebarClass}
      data-testid="sidebar"
    >
      <div className="sideNav">
        <Sidenav appearance="subtle" expanded={expanded || mobileOpen}>
          <div content="Global controls 1.0" className="rs-sidenav-toggle">
            <h3 className="sidenav-header">{tenantConfig.name}</h3>
            <button
              aria-label="Collapse"
              type="button"
              className="rs-sidenav-toggle-button rs-btn-icon rs-btn-icon-placement-left rs-btn rs-btn-default"
              onClick={clickToggler}
            >
              {expanded ? (
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 12 32"
                  fill="currentColor"
                  aria-hidden="true"
                  focusable="false"
                  className="rs-icon"
                  aria-label="angle left"
                  data-category="legacy"
                >
                  <path d="M11.196 9.714a.612.612 0 01-.179.411l-7.018 7.018 7.018 7.018c.107.107.179.268.179.411s-.071.304-.179.411l-.893.893c-.107.107-.268.179-.411.179s-.304-.071-.411-.179L.981 17.555c-.107-.107-.179-.268-.179-.411s.071-.304.179-.411l8.321-8.321c.107-.107.268-.179.411-.179s.304.071.411.179l.893.893c.107.107.179.25.179.411z" />
                </svg>
              ) : (
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 12 32"
                  fill="currentColor"
                  aria-hidden="true"
                  focusable="false"
                  className="rs-icon"
                  aria-label="angle right"
                  data-category="legacy"
                >
                  <path d="M.804 22.286a.612.612 0 01.179-.411l7.018-7.018-7.018-7.018c-.107-.107-.179-.268-.179-.411s.071-.304.179-.411l.893-.893c.107-.107.268-.179.411-.179s.304.071.411.179l8.321 8.321c.107.107.179.268.179.411s-.071.304-.179.411l-8.321 8.321c-.107.107-.268.179-.411.179s-.304-.071-.411-.179l-.893-.893c-.107-.107-.179-.25-.179-.411z" />
                </svg>
              )}
            </button>
          </div>
          <Sidenav.Body>
            <Nav
              activeKey={activeKey}
              onSelect={(key) => {
                setActiveKey(key)
                onMobileClose?.()
              }}
            >
              {visibleItems.map(({ id, name, link, icon }) => (
                <NavItem
                  key={id}
                  id={id}
                  name={name}
                  link={link}
                  icon={icon}
                  active={activeKey === id}
                />
              ))}
              <Divider />
            </Nav>
          </Sidenav.Body>
          {/* Sign-out anchored at the very bottom of the sidebar — sits
              outside Sidenav.Body so it stays pinned even when the menu
              list scrolls, and never inherits the "active" highlight. */}
          <div className="sidenav-footer">
            <button
              type="button"
              className="sidenav-signout"
              onClick={() => setSignOutOpen(true)}
              title="Sign out"
            >
              <SignOutIcon className="rs-sidenav-item-icon rs-icon" />
              <span className="sidenav-item__text">Sign out</span>
            </button>
          </div>
        </Sidenav>
      </div>

      <ConfirmDialog
        open={signOutOpen}
        type="warning"
        icon={SignOutIcon}
        title="Sign out"
        message="Are you sure you want to sign out?"
        confirmText="Sign out"
        cancelText="Cancel"
        onConfirm={handleSignOut}
        onCancel={() => setSignOutOpen(false)}
      />
    </Sidebar>
  )
}

export default SideNav
